import React from 'react';
import { charSets, fontSources, typeSpecs, type TypeSpec } from '../data/typeSheet';

/**
 * /type-sheet — built purely for Figma export.
 *
 * Every specimen is a single text layer styled with INLINE CSS at its exact
 * desktop value, so the export carries the real size, weight, line height
 * and tracking. No responsive classes, no nested wrappers around the text
 * itself — paste into Figma, select a layer, create the style, done.
 */
export function TypeSheet() {
  return <div className="w-full bg-paper" data-id="element-1058" data-mp="105fa7qe2j7yg">
      <div className="mx-auto max-w-[1400px] px-6 py-20 md:py-24" data-id="element-1059" data-mp="104dydjp8ahfo">
        {/* Header */}
        <header className="max-w-3xl" data-id="element-1060" data-mp="14xgkj6roeolu">
          <p className="type-eyebrow text-[11px] text-pennant" data-id="element-1061" data-mp="14yi6456bitht">
            Ecoffee Cup® SA · Type sheet
          </p>
          <h1 className="type-display mt-5 text-5xl text-coffee md:text-6xl" data-id="element-1062" data-mp="1dx3nvy3yu4hj">
            Fifteen text styles, at exact values.
          </h1>
          <p className="type-body mt-6 text-base text-ink/65" data-id="element-1063" data-mp="1ix80ve5o0h30">
            Each specimen below is one text layer set with literal pixel
            values, so it survives the Figma export intact. Work down the
            page: select the sample, check it against the spec beside it, then
            create a text style using the name in Purple Pennant.
          </p>
        </header>

        {/* Fonts to install */}
        <section className="mt-20" data-id="element-1064" data-mp="32za8hn1fd9wh">
          <h2 className="type-eyebrow text-[10px] text-coffee/45" data-id="element-1065" data-mp="4g3hiaad7dns">
            Before you start — fonts
          </h2>
          <div className="mt-6 overflow-hidden rounded-3xl border border-oat" data-id="element-1066" data-mp="3vluyaj295dxx">
            <table className="w-full border-collapse text-left" data-id="element-1067" data-mp="3qvo6ybgcvfbz">
              <thead data-id="element-1068" data-mp="3qttpj74am5cn">
                <tr className="bg-paper-warm" data-id="element-1069" data-mp="3qtblvpfawoup">
                  {['Font', 'Used for', 'Weights', 'Where to get it'].map(h => <th key={h} scope="col" className="type-eyebrow px-6 py-4 text-[9px] text-coffee/55" data-id="element-1070" data-mp="n7ctqrbisgj2">
                        {h}
                      </th>)}
                </tr>
              </thead>
              <tbody data-id="element-1071" data-mp="2gr4p3r7axo46">
                {fontSources.map(f => <tr key={f.name} className="border-t border-oat align-top" data-id="element-1072" data-mp="22xdppbnabkm6">
                    <td className="px-6 py-5" data-id="element-1073" data-mp="27vyhqnsduxzj">
                      <p className="type-ui text-base text-coffee" data-id="element-1074" data-mp="27uncejjahi6b">{f.name}</p>
                      <p className="type-body mt-1 text-xs text-ink/50" data-id="element-1075" data-mp="1syeepmf1rcw0">
                        {f.licence}
                      </p>
                    </td>
                    <td className="type-body px-6 py-5 text-sm text-ink/65" data-id="element-1076" data-mp="1jzt1m6scgq2m">
                      {f.role}
                    </td>
                    <td className="type-body px-6 py-5 text-sm text-ink/65" data-id="element-1077" data-mp="1oynrfsoqbew8">
                      {f.weights}
                    </td>
                    <td className="type-body px-6 py-5 text-sm text-ink/65" data-id="element-1078" data-mp="3jc576tiw77gp">
                      {f.where}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <p className="type-body mt-5 max-w-3xl text-sm text-ink/55" data-id="element-1079" data-mp="3ohs9xvkzyzkv">
            If the client holds a Gotham licence, substitute Gotham Light,
            Medium and Bold wherever Montserrat appears below — the sizes,
            line heights and tracking stay exactly the same. Phosphate,
            Budmojiggler and Craque are retail and social only, and never
            appear on web.
          </p>
        </section>

        {/* Specimens */}
        <section className="mt-20" data-id="element-1080" data-mp="b470cjeabvy1">
          <h2 className="type-eyebrow text-[10px] text-coffee/45" data-id="element-1081" data-mp="p2dp9a19allk">
            The styles
          </h2>
          <div className="mt-6 overflow-hidden rounded-3xl border border-oat bg-paper" data-id="element-1082" data-mp="l00lus9zezry">
            {typeSpecs.map((spec, i) => <Specimen key={spec.figma} spec={spec} first={i === 0} data-id="element-1083" data-mp="2qtqslunkslfi" data-mpi={i} />)}
          </div>
        </section>

        {/* Character sets */}
        <section className="mt-20" data-id="element-1084" data-mp="2o92k8325bafy">
          <h2 className="type-eyebrow text-[10px] text-coffee/45" data-id="element-1085" data-mp="2o80woz3y0vhc">
            Character sets
          </h2>
          <p className="type-body mt-4 max-w-2xl text-sm text-ink/60" data-id="element-1086" data-mp="2t6c6j9u8n5zk">
            Paste these too — they confirm both families loaded properly, and
            give you the ® and · characters the brand uses often.
          </p>

          <div className="mt-8 grid gap-6 lg:grid-cols-2" data-id="element-1087" data-mp="3267mrt2nhr6g">
            {[{
            name: 'DM Serif Display',
            family: typeSpecs[0].family,
            weight: 400
          }, {
            name: 'Montserrat (Gotham sub.)',
            family: typeSpecs[5].family,
            weight: 500
          }].map(font => <div key={font.name} className="rounded-3xl border border-oat bg-paper-warm p-8" data-id="element-1088" data-mp="2egwii1m3denk">
                <p className="type-eyebrow text-[9px] text-pennant" data-id="element-1089" data-mp="1luvcysye0b3b">
                  {font.name}
                </p>
                <div className="mt-6 space-y-5" data-id="element-1090" data-mp="1qsdcxtufffz6">
                  {charSets.map(set => <div key={set.label} data-id="element-1091" data-mp="1j8ukyy37gceg">
                      <p className="type-eyebrow text-[8px] text-coffee/40" data-id="element-1092" data-mp="1j7t7kmypvdtk">
                        {set.label}
                      </p>
                      <p className="mt-2 break-words" style={{
                  fontFamily: font.family,
                  fontWeight: font.weight,
                  fontSize: '22px',
                  lineHeight: '32px',
                  letterSpacing: '-0.01em',
                  color: '#3B302F'
                }} data-id="element-1093" data-mp="19b6nw1i4mst4">
                        {set.chars}
                      </p>
                    </div>)}
                </div>
              </div>)}
          </div>
        </section>

        {/* How to */}
        <section className="mt-20 rounded-4xl bg-coffee p-9 text-paper md:p-14" data-id="element-1094" data-mp="hmczvhlhgb8z">
          <h2 className="type-display text-3xl text-paper md:text-4xl" data-id="element-1095" data-mp="cm8ryfyn4oud">
            Turning these into Figma text styles
          </h2>
          <ol className="mt-8 grid gap-6 md:grid-cols-2" data-id="element-1096" data-mp="6z6pyotzyxbr">
            {['Export this screen on its own from the Canvas, so nothing else competes for layers.', 'Install DM Serif Display and Montserrat (or Gotham) before pasting — Figma cannot match a font it does not have.', 'Paste, then select a sample text layer. Check the size, line height and letter-spacing in the right panel against the spec column.', 'Letter-spacing pastes as a percentage: -3% for display, +20% for eyebrows, +9% for CTAs.', 'With the layer selected, click the four-dot style icon beside Typography, then Create style, and name it exactly as shown in Purple Pennant.', 'Repeat down the sheet. Fifteen styles cover every page in the site.'].map((step, i) => <li key={i} className="flex gap-5" data-id="element-1097" data-mp="19ep2y4arhei5" data-mpi={i}>
                <span className="type-eyebrow shrink-0 pt-1 text-[10px] text-paper/45" data-id="element-1098" data-mp="2u4c9srevtmqw">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="type-body text-sm text-paper/75" data-id="element-1099" data-mp="2p4pveenbi0gi">{step}</span>
              </li>)}
          </ol>
        </section>
      </div>
    </div>;
}
function Specimen({
  spec,
  first
}: {
  spec: TypeSpec;
  first: boolean;
}) {
  return <div className={`grid gap-8 px-8 py-10 md:grid-cols-[minmax(0,1fr)_300px] md:px-12 md:py-14 ${first ? '' : 'border-t border-oat'}`} data-id="element-1100" data-mp="2b4ycbzhy1tvn">
      {/* The specimen — one clean text layer */}
      <p style={{
      fontFamily: spec.family,
      fontWeight: spec.weight,
      fontSize: `${spec.size}px`,
      lineHeight: `${spec.lineHeight}px`,
      letterSpacing: `${spec.tracking}em`,
      textTransform: spec.uppercase ? 'uppercase' : 'none',
      fontStyle: spec.figma === 'Display/Quote' ? 'italic' : 'normal',
      color: spec.colour,
      margin: 0
    }} data-id="element-1101" data-mp="2k6elk3x0vpya">
        {spec.sample}
      </p>

      {/* Spec column */}
      <dl className="space-y-2 md:border-l md:border-oat md:pl-10" data-id="element-1102" data-mp="jh8rtyzy600k">
        <Row label="Style name" value={spec.figma} highlight data-id="element-1103" data-mp="njnaoj3m8zru" />
        <Row label="Font" value={spec.familyName} data-id="element-1104" data-mp="ni2b2f2g2qn4" />
        <Row label="Weight" value={`${spec.weightName} ${spec.weight}`} data-id="element-1105" data-mp="savijapasd2w" />
        <Row label="Size" value={spec.mobileSize ? `${spec.size}px · ${spec.mobileSize}px mobile` : `${spec.size}px`} data-id="element-1106" data-mp="sc6onh67twx6" />
        <Row label="Line height" value={`${spec.lineHeight}px`} data-id="element-1107" data-mp="12aco7s4juqyv" />
        <Row label="Letter-spacing" value={`${spec.tracking > 0 ? '+' : ''}${Math.round(spec.tracking * 100)}%`} data-id="element-1108" data-mp="1784c8b22psxs" />
        <Row label="Case" value={spec.uppercase ? 'ALL CAPS' : 'Sentence'} data-id="element-1109" data-mp="38zr92t8nci26" />
        <Row label="Colour" value={spec.colour} data-id="element-1110" data-mp="3d0b5fulebhn9" />
      </dl>
    </div>;
}
function Row({
  label,
  value,
  highlight = false
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return <div className="flex items-baseline justify-between gap-4" data-id="element-1111" data-mp="30qlh2ctf7p9q">
      <dt className="type-eyebrow shrink-0 text-[8px] text-coffee/40" data-id="element-1112" data-mp="30o8q5a5oq4kj">
        {label}
      </dt>
      <dd className={`type-ui text-right text-xs ${highlight ? 'text-pennant' : 'text-coffee'}`} data-id="element-1113" data-mp="2vp5x6o2zbosx">
        {value}
      </dd>
    </div>;
}